// frontend/src/infrastructure/debug/AppDebugLog.ts
// Globalny dziennik checkpointów do debugowania na telefonie.
// Zbiera logi w pamięci i pozwala wyświetlić je jako overlay w aplikacji.
// W produkcji można wyłączyć przez usunięcie komponentu lub flagę.

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'checkpoint'

export interface LogEntry {
  id: number
  timestamp: number
  level: LogLevel
  tag: string
  message: string
  data?: unknown
}

const MAX_ENTRIES = 300

let nextId = 1
const entries: LogEntry[] = []
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach(cb => {
    try {
      cb()
    } catch {
      // listener nie może wywrócić loggera
    }
  })
}

export function subscribeDebugLog(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function getDebugLogEntries(): LogEntry[] {
  return entries.slice()
}

export function clearDebugLog(): void {
  entries.length = 0
  notify()
}

function push(level: LogLevel, tag: string, message: string, data?: unknown) {
  const entry: LogEntry = {
    id: nextId++,
    timestamp: Date.now(),
    level,
    tag,
    message,
    data: data === undefined ? undefined : safeClone(data),
  }
  entries.unshift(entry)
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES

  // Równolegle wypisuj w konsoli, żeby dało się podłączyć chrome://inspect
  const prefix = `[${tag}] ${message}`
  if (level === 'error') console.error(prefix, data ?? '')
  else if (level === 'warn') console.warn(prefix, data ?? '')
  else if (level === 'checkpoint') console.info(`✓ ${prefix}`, data ?? '')
  else console.log(prefix, data ?? '')

  notify()
}

export function logDebug(tag: string, message: string, data?: unknown) {
  push('debug', tag, message, data)
}

export function logInfo(tag: string, message: string, data?: unknown) {
  push('info', tag, message, data)
}

export function logWarn(tag: string, message: string, data?: unknown) {
  push('warn', tag, message, data)
}

export function logError(tag: string, message: string, data?: unknown) {
  push('error', tag, message, data)
}

/**
 * Główna funkcja checkpointu. Używaj w kluczowych momentach:
 * - Vosk: model loaded, start, partial, final, error
 * - PTT: event received, press/release/click
 * - Parser: input text, parsed fields
 * - Sync: queued, started, success, failed
 */
export function checkpoint(tag: string, message: string, data?: unknown) {
  push('checkpoint', tag, message, data)
}

function safeClone(value: unknown): unknown {
  try {
    if (value instanceof Error) {
      return { message: value.message, name: value.name, stack: value.stack }
    }
    return JSON.parse(JSON.stringify(value))
  } catch {
    return String(value)
  }
}

/**
* Eksportuje logi jako tekst do schowka / udostępniania.
*/
export function exportDebugLog(): string {
  return getDebugLogEntries()
    .reverse()
    .map(e => {
      const time = new Date(e.timestamp).toISOString().slice(11, 23)
      const data = e.data !== undefined ? ` ${JSON.stringify(e.data)}` : ''
      return `${time} [${e.level.toUpperCase()}] ${e.tag}: ${e.message}${data}`
    })
    .join('\n')
}
